import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  LoadingSpinner,
  PageContainer,
} from '@startupquickstart/frontend';
import DOMPurify from 'dompurify';
import { ArrowLeft, ExternalLink, MessageCircle } from 'lucide-react';
import MarkdownIt from 'markdown-it';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTicket } from '@/api/support';
import { StatusBadge } from '@/components/support';

// markdown-it with `html: false` escapes raw HTML from the source so the
// only markup we emit comes from markdown syntax (links, lists, code,
// etc.). DOMPurify on top guards against any future option drift or a
// markdown-it bug — defence in depth for content that ultimately
// originated from Linear (i.e. a teammate's keyboard, not a user we
// fully trust).
const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

function MarkdownBody({ source }: { source: string | null | undefined }) {
  const html = useMemo(() => {
    if (!source) return '';
    return DOMPurify.sanitize(md.render(source), {
      ADD_ATTR: ['target', 'rel'],
    });
  }, [source]);
  if (!source) return null;
  return (
    <div
      className="prose prose-sm max-w-none dark:prose-invert break-words"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/**
 * Read-only ticket detail. Comments are fetched alongside but rendering
 * stays display-only in v1 — replies happen in Linear (and parents get
 * Linear's native email notifications when someone replies on their issue).
 */
export function TicketDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: ticket, isLoading, isError, error } = useTicket(id);

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <ErrorState
          title="Could not load ticket"
          description={
            error instanceof Error ? error.message : 'Unknown error'
          }
        />
      </PageContainer>
    );
  }

  if (!ticket) {
    return (
      <PageContainer>
        <EmptyState
          title="Ticket not found"
          description="This ticket may have been deleted, or it was filed by someone else."
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2"
              onClick={() => navigate('/internal/support')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              All tickets
            </Button>
            <div className="flex flex-wrap items-center gap-2">
              <code className="rounded bg-muted px-2 py-0.5 text-sm">
                {ticket.identifier}
              </code>
              <StatusBadge state={ticket.state} />
              {ticket.labels.map((l) => (
                <Badge key={l.id} variant="outline">
                  {l.name}
                </Badge>
              ))}
            </div>
            <h1 className="text-3xl font-semibold tracking-tight">
              {ticket.title}
            </h1>
          </div>
          <Button
            variant="outline"
            onClick={() =>
              window.open(ticket.url, '_blank', 'noopener,noreferrer')
            }
          >
            View in Linear
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            {ticket.description ? (
              <MarkdownBody source={ticket.description} />
            ) : (
              <p className="text-sm text-muted-foreground">No description.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Comments ({ticket.comments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {ticket.comments.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No comments yet. Replies happen in Linear — open the ticket
                there to discuss.
              </p>
            ) : (
              ticket.comments.map((c) => (
                <div
                  key={c.id}
                  className="rounded-md border bg-muted/30 p-3 text-sm"
                >
                  <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{c.user.name}</span>
                    <span>{new Date(c.createdAt).toLocaleString()}</span>
                  </div>
                  <MarkdownBody source={c.body} />
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
